/**
 * meterCompletion — completion + abort animations + engine watchers.
 *
 * Per A6 §useMeterRenderer Option A: pure-function workshop. The shell
 * owns the closure bag; this module's three exports mutate the shared
 * `MeterCompletionState` so the rAF tick gates cleanly while a play
 * runs:
 *
 * - `playCompletion(deps)` — 4π whirl (2.5s, easeOutCubic) + settle
 *   from upload-proportional → download-proportional angle (550ms).
 * - `playAbortReset(deps)` — needle back to t=0 (600ms easeOutCubic);
 *   idle loop resumes.
 * - `paintCompleteHold(bag, getAmount)` — static remount paint for
 *   already-complete state (router-back / HMR mid-session).
 * - `watchMeterEngine(props, deps)` — currentEnginePhase + isRunning
 *   watchers that drive the play decisions.
 */

import { watch } from "vue";
import type { SmoothProgress } from "@mkbabb/keyframes.js";
import { NumericAnimation, SpringProgress } from "@mkbabb/keyframes.js";
// value.js is the constellation's scalar-math authority (AV.W11b suffusion).
// `lerp(start, end, t)` — range first, then the fraction (note: the retired
// local hoist took `(t, start, end)`; the call sites below are reordered).
import { lerp } from "@mkbabb/value.js";
import { easeOutCubic } from "@design";
import { metricProgress } from "../../ui/meter/canvas/meter/config.ts";
import { drawAllRings, drawRings } from "../../ui/meter/canvas/meter/rings.ts";
import { drawDial } from "../../ui/meter/canvas/meter/dial.ts";
import type { MeterGeometryBag } from "./meterGeometry.ts";
import { drawFrame } from "./paintMeter.ts";

/**
 * Mutable gate slots the shell shares with the completion lane.
 * `isPlayingCompletion` + `isCompleted` gate the rAF tick; `lastStateName`
 * lets the abort path restore the idle context.
 */
export interface MeterCompletionState {
    isPlayingCompletion: boolean;
    isCompleted: boolean;
    lastStateName: string | undefined;
}

/**
 * Optional choreography-signal slot — when wired, the watcher arms the
 * signal on the `isRunning` rising edge and marks it complete after the
 * `playCompletion` (or `playAbortReset`) play resolves. The signal is
 * the seam the chassis cascade reads via `useEngineState` to hold the
 * `running` layout for the duration of the whirl (AJ-W1-θ Path θ-A.1.a).
 *
 * The slot is optional so consumers without a chassis gate (tests,
 * non-speedtest hosts) can mount the renderer without wiring it.
 */
export interface ChoreographySignal {
    reset: () => void;
    markComplete: () => void;
}

/** Deps handle the completion + watcher paths thread through. */
export interface MeterCompletionDeps {
    bag: MeterGeometryBag;
    state: MeterCompletionState;
    smooth: SmoothProgress;
    getAmount: (stateName: string, kind?: string) => number;
    stopLoop: () => void;
    startLoop: () => void;
    choreographySignal?: ChoreographySignal;
    /**
     * AV.W14.3 — the idle-on-STOP settle-back acknowledgement. Called once
     * the abort settle has landed the needle at the at-rest hero pose, just
     * before the idle breath loop resumes. The renderer wires it to ONE
     * ring-pulse arm (the SAME at-rest vocabulary, no new mechanism — see
     * DDR-AV-W14-BREATH), so post-STOP idle reads DISTINCT from a
     * byte-identical cold idle (which has no recent pulse). PRM-parked at
     * the renderer seam (the pulse's own `respectReducedMotion`). Optional
     * so a host without a pulse (tests) can mount the renderer unwired.
     */
    settleBack?: () => void;
}

/**
 * AV.W14.3 — the abort settle-back window (ms). The `isRunning` falling
 * edge that aborts a run plays a SpringProgress settle of the needle back
 * to the at-rest hero pose over this window; the spring's gentle landing is
 * a distinct motion signature from the cold idle's static breath frame.
 */
export const SETTLE_BACK_MS = 520;

/** Static remount paint — rainbow rings + needle parked at the
 *  download-peak angle. Mirrors `playCompletion`'s final hold. */
export function paintCompleteHold(
    bag: MeterGeometryBag,
    getAmount: (stateName: string, kind?: string) => number,
): void {
    const { canvas, config, rings, dial } = bag;
    if (!canvas || !config || !rings || !dial) return;
    const downloadValue = getAmount("download");
    const restAngle =
        downloadValue > 0
            ? lerp(
                  config.startAngle,
                  config.endAngle,
                  metricProgress("download", downloadValue),
              )
            : config.endAngle;
    canvas.clear();
    drawAllRings(canvas, rings, ["ping", "download", "upload"], 1);
    drawDial(canvas, dial, restAngle);
}

export async function playCompletion(deps: MeterCompletionDeps): Promise<void> {
    const { bag, state, getAmount, stopLoop, choreographySignal } = deps;
    const { canvas, config, rings, dial } = bag;
    if (!canvas || !config || !rings || !dial || !state.lastStateName) {
        // Bail with the signal disarmed — there is no whirl to wait for,
        // so the chassis should advance immediately. Without this the
        // gate would stick armed (set by the rising-edge watcher) and
        // the chassis would freeze in `running`.
        choreographySignal?.markComplete();
        return;
    }
    state.isPlayingCompletion = true;
    const c = canvas,
        cfg = config,
        r = rings,
        d = dial;

    // Spin: rainbow holds (drawAllRings) while needle whirls past
    // endAngle. Single-phase drawRings would only paint the final
    // active phase — we want the full ring stack lit.
    await new NumericAnimation([{ t: 0 }, { t: 1 }], {
        timingFunction: easeOutCubic,
        duration: 2500,
    }).play(({ t }) => {
        c.clear();
        drawAllRings(c, r, ["ping", "download", "upload"], 1);
        drawDial(c, d, cfg.endAngle + t * Math.PI * 4);
    });

    // Settle: animate FROM the upload-proportional angle (the live
    // angle held just before completion) TO the download-proportional
    // angle (the headline answer) over 550 ms, easeOutCubic. When
    // download is 0/null, fall back to the geometric `endAngle`.
    const downloadValue = getAmount("download");
    const uploadValue = getAmount("upload");
    const restAngle =
        downloadValue > 0
            ? lerp(
                  cfg.startAngle,
                  cfg.endAngle,
                  metricProgress("download", downloadValue),
              )
            : cfg.endAngle;
    const fromAngle = lerp(
        cfg.startAngle,
        cfg.endAngle,
        metricProgress("upload", uploadValue),
    );
    await new NumericAnimation([{ a: fromAngle }, { a: restAngle }], {
        timingFunction: easeOutCubic,
        duration: 550,
    }).play(({ a }) => {
        c.clear();
        drawAllRings(c, r, ["ping", "download", "upload"], 1);
        drawDial(c, d, a);
    });

    state.isPlayingCompletion = false;
    state.isCompleted = true;
    stopLoop();
    // Final hold — rainbow rings + needle parked at download peak
    // (or endAngle fallback).
    c.clear();
    drawAllRings(c, r, ["ping", "download", "upload"], 1);
    drawDial(c, d, restAngle);

    // AJ-W1-θ Path θ-A.1.a: the whirl + settle has resolved. Disarm the
    // chassis gate so `data-engine-state` advances `running → complete`.
    // Until this signal fires, the chassis stays in `running` and the
    // meter holds its slot — the whirl plays in its proper visual home
    // instead of beside a half-collapsed layout.
    choreographySignal?.markComplete();
}

/**
 * Wire the engine watchers that gate completion + abort. Must run
 * inside a host composable / `<script setup>` boundary so the watchers
 * self-dispose via Vue's effect scope.
 */
export function watchMeterEngine(
    props: {
        readonly currentEnginePhase: string | undefined;
        readonly isRunning: boolean;
        readonly speedtestData: unknown;
        readonly getSpeedtestStateAmount: (
            stateName: string,
            kind?: string,
        ) => number;
    },
    deps: MeterCompletionDeps,
): void {
    const { state, smooth, stopLoop, startLoop } = deps;
    // `state.lastStateName` has ONE writer — the rAF tick in
    // `useMeterRenderer` records each measured phase as it paints it.
    // The completion lane only READS it (to choose the completion vs
    // abort play) and the abort/run-start paths CLEAR it. A second
    // `currentEnginePhase` watcher writing the same value was redundant
    // ("two truths" for one slot) — the renderer's tick is the single
    // source, so the watcher is retired (AG-EA7).
    watch(
        () => props.isRunning,
        (running) => {
            if (running) {
                // Rising edge = a true clean slate. `lastStateName` joins the
                // other gate resets: a prior run leaves it charged at "upload"
                // (`playCompletion` has no reason to clear it), and the rAF
                // `tick()` fallback would otherwise paint that stale phase's
                // colour for the opening pre-ping frames of the re-test.
                state.isPlayingCompletion = false;
                state.isCompleted = false;
                state.lastStateName = undefined;
                smooth.reset(0);
                // AJ-W1-θ Path θ-A.1.a: arm the chassis gate so once the test
                // resolves `allComplete`, the chassis holds `running` until
                // the meter's whirl + settle resolves. Symmetric to the
                // `markComplete` calls inside `playCompletion` /
                // `playAbortReset` so a remount with no prior arm reads `true`
                // and the chassis paints `complete` immediately (HMR / back-
                // nav onto already-finished state).
                deps.choreographySignal?.reset();
                startLoop();
                return;
            }
            // The idle breathing oscillator keeps the loop alive on its own.
            // Completion / abort animations gate via `state.isPlayingCompletion`.
            if (state.lastStateName && deps.bag.config && props.speedtestData) {
                stopLoop();
                const amount = props.getSpeedtestStateAmount(
                    state.lastStateName,
                );
                if (amount > 0) {
                    void playCompletion(deps);
                } else {
                    void playAbortReset(deps);
                }
            } else {
                // No play to run — disarm the gate immediately so the chassis
                // isn't stuck on `running`. The abort path lands here when
                // `progress.reset()` has already nulled `data.value`.
                deps.choreographySignal?.markComplete();
            }
        },
    );
}

export async function playAbortReset(deps: MeterCompletionDeps): Promise<void> {
    const { bag, state, smooth, startLoop, choreographySignal, settleBack } =
        deps;
    const { canvas, config, rings, dial } = bag;
    if (!canvas || !config || !rings || !dial) {
        choreographySignal?.markComplete();
        return;
    }
    state.isPlayingCompletion = true;
    const c = canvas,
        cfg = config,
        r = rings,
        d = dial;
    const sn = state.lastStateName;
    const startT = smooth.current;

    // AV.W14.3 — the settle-back. The needle eases its fill `startT → 0`
    // on a SPRING (a gentle, slightly-overshooting landing) rather than the
    // prior linear easeOutCubic, so the abort reads as the instrument
    // SETTLING back to its hero rest pose, not a flat drain. `SpringProgress`
    // `.play(onFrame)` runs the library's managed rAF until settled (the
    // canonical clock-owner stepper); `respectReducedMotion` snaps the spring
    // straight to its target so PRM resolves instantly with no spring motion.
    // The settle is the motion half of "post-STOP idle ≠ cold idle"; the
    // pulse arm below is the standing-frame half.
    const settle = SpringProgress.fromDuration({
        visualDuration: SETTLE_BACK_MS / 1000,
        bounce: 0.12,
        initial: startT,
        respectReducedMotion: true,
    });
    await new Promise<void>((resolve) => {
        let done = false;
        settle.target = 0;
        settle.play((t) => {
            const fill = Math.max(t, 0);
            c.clear();
            drawRings(c, r, sn, fill);
            drawDial(c, d, lerp(cfg.startAngle, cfg.endAngle, fill));
            if (settle.settled && !done) {
                done = true;
                settle.dispose();
                resolve();
            }
        });
    });

    state.isPlayingCompletion = false;
    state.isCompleted = false;
    smooth.reset(0);
    state.lastStateName = undefined;
    drawFrame(bag, undefined, 0, performance.now());
    // AV.W14.3 — arm the at-rest settle acknowledgement: ONE ring-pulse in
    // the at-rest vocabulary fires now, so the post-STOP idle frame carries
    // a fresh bloom that a cold idle (no recent arm — the next jittered arm
    // is ~20s out) does NOT. This is the standing-frame distinction the W14
    // hard-gate's paired capture (`post-stop-idle` vs `cold-idle`) asserts.
    // PRM-parked at the renderer seam.
    settleBack?.();
    // Restart the always-on idle loop so breathing resumes.
    startLoop();
    // AJ-W1-θ Path θ-A.1.a: the abort reset has resolved. Disarm the
    // chassis gate so it can progress past `running` (in practice the
    // chassis reads `idle` because abort cleared metric states).
    choreographySignal?.markComplete();
}
